import React, {forwardRef, useEffect, useId, useImperativeHandle, useRef} from 'react';
import {useDroppable} from '@dnd-kit/react';
import {defaultCollisionDetection} from '@dnd-kit/collision';
import {CollisionPriority} from '@dnd-kit/abstract';

import {DragMetadata, DroppableProps} from './types';

type DropTargetProps = React.HTMLAttributes<HTMLDivElement> &
  DroppableProps & {ref?: React.Ref<HTMLDivElement>; children?: React.ReactNode};

/**
 * Component that allows dropping draggable entries with a specified entry type handler
 */
export const DropZone = forwardRef<HTMLDivElement, DropTargetProps>(function DropTarget(props, forwardedRef) {
  const ref = useRef<HTMLDivElement>(null);
  const dragId = useId();

  const {isDropTarget} = useDroppable({
    element: ref.current,
    collisionDetector: defaultCollisionDetection,
    collisionPriority: CollisionPriority.Normal,
    id: `${dragId}-droppable`,
    data: props.data,
    accept(source) {
      if (!source.type || typeof source.type !== `string` || !props.accepts) {
        return false;
      }

      const fn = props.accepts[source.type as DragMetadata[`type`]];
      if (!fn) {
        return false;
      }

      return fn(source.data as DragMetadata) ?? false;
    },
  });

  useEffect(() => {
    ref.current?.toggleAttribute(`data-dnd-is-drop-target`, isDropTarget);
  }, [isDropTarget]);

  useImperativeHandle(forwardedRef, () => ref.current!);

  return (
    <div {...props} ref={ref}>
      {props.children}
    </div>
  );
});
